import React from "react";
import usePageTitle from "../hooks/usePageTitle";

const PrivacyPolicy = () => {
  usePageTitle("Privacy Policy - Agrolinka");

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
        <h1 className="text-3xl font-bold text-agBlack mb-2">Privacy Policy</h1>
        <p className="text-sm text-agLightGray mb-8">
          Last updated: 21/1/2026
        </p>

        <div className="space-y-6 text-gray-700">
          <p>
            At Agrolinka ("we", "us", "our"), we are committed to protecting
            the privacy of our customers and visitors to{" "}
            <a
              href="https://agrolinka.com/"
              className="text-agGreen hover:underline"
            >
              https://agrolinka.com/
            </a>{" "}
            ("Website"). This Privacy Policy explains how we collect, use,
            disclose, and safeguard your personal information when you visit our
            Website, make purchases, create an account, or interact with us
            online. By using our Website and services, you agree to the terms of
            this Privacy Policy.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              1. Information We Collect
            </h2>
            <p className="mb-3">
              We collect personal information that you voluntarily provide when
              you:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>Create an account or place an order</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact customer support</li>
              <li>Use our Website features</li>
            </ul>
            <p className="mb-2">This information may include:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Personal Data:</strong> name, email address, phone
                number, shipping and billing addresses.
              </li>
              <li>
                <strong>Account Data:</strong> username, password (encrypted),
                order history.
              </li>
              <li>
                <strong>Payment Information:</strong> details necessary to
                process your purchase (we do not store full payment card
                numbers).
              </li>
              <li>
                <strong>Technical Data:</strong> IP address, browser type,
                device type, pages visited and interaction data.
              </li>
              <li>
                <strong>Marketing and Communications Data:</strong> preferences
                and consent for receiving newsletters or promotional emails.
              </li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              2. How We Use Your Information
            </h2>
            <p className="mb-2">
              We use your personal information for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Order Processing:</strong> To process and fulfill your
                orders, including delivery and notifications.
              </li>
              <li>
                <strong>Communication:</strong> To respond to inquiries, send
                order updates, and manage customer service.
              </li>
              <li>
                <strong>Marketing:</strong> To send promotional emails only if
                you opt in and allow you to unsubscribe at any time.
              </li>
              <li>
                <strong>Improving Services:</strong> To optimize our Website,
                understand usage trends, and improve user experience.
              </li>
              <li>
                <strong>Compliance and Security:</strong> To protect against
                fraud, comply with legal obligations, and enforce our policies.
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              3. Cookies and Tracking Technologies
            </h2>
            <p>
              We (and trusted third parties) may use cookies, web beacons, and
              similar technologies to collect non-personal data about how you
              interact with our Website (e.g., pages visited, time spent).
              Cookies help us enhance functionality, personalize experience, and
              analyze trends. You can set your browser to refuse cookies, but
              this may affect Website functionality.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              4. Sharing Your Data
            </h2>
            <p className="mb-3">
              We do not sell your personal information. We may share your data
              with:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Service Providers</strong> (e.g., payment processors,
                delivery partners, analytics tools) for business operations.
              </li>
              <li>
                <strong>Legal Authorities:</strong> when required by law or to
                protect Agrolinka's rights.
              </li>
              <li>
                <strong>Business Transfers:</strong> in connection with a sale,
                merger, or reorganization.
              </li>
            </ul>
            <p className="mt-3">
              All third parties are required to protect your information and
              only use it for the purposes we specify.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              5. Security of Your Information
            </h2>
            <p>
              We implement reasonable technical and administrative safeguards to
              protect your personal information against unauthorized access,
              alteration, or disclosure. However, no method of electronic
              transmission or storage is 100% secure. We cannot guarantee
              absolute security.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              6. Data Retention
            </h2>
            <p>
              We retain your personal information only as long as necessary to
              fulfill the purposes outlined in this Privacy Policy or as
              required by law. This includes keeping order records and
              transaction history for legal, tax, and accounting purposes.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              7. Your Rights
            </h2>
            <p className="mb-2">Where applicable, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Correct or update your information</li>
              <li>Request deletion of your data</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent previously given</li>
            </ul>
            <p className="mt-3">
              You can exercise these rights by contacting us at the details
              listed below.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              8. Third-Party Websites
            </h2>
            <p>
              Our Website may contain links to other sites. We are not
              responsible for the content or privacy practices of external
              sites. We encourage you to read the privacy policies of any site
              you visit.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              9. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. The updated
              policy will be posted on this page with a revised "Last updated"
              date. Your continued use after changes constitute acceptance of
              the new terms.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              10. Contact Us
            </h2>
            <p className="mb-3">
              If you have questions about this Privacy Policy or want to
              exercise your data rights, please contact us:
            </p>
            <div className="bg-agBg p-4 rounded-md space-y-2">
              <p>
                <strong>📧 Email:</strong>{" "}
                <a
                  href="mailto:info@agrolinka.com"
                  className="text-agGreen hover:underline"
                >
                  info@agrolinka.com
                </a>
              </p>
              <p>
                <strong>📞 Phone:</strong>{" "}
                <a
                  href="tel:+2348065165064"
                  className="text-agGreen hover:underline"
                >
                  +234 806 516 5064
                </a>
              </p>
              <p>
                <strong>🏢 Address:</strong> 13 Akure Street, War College
                Estate, Gwarimpa, Abuja, Nigeria
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
